import { createSelector, createFeatureSelector } from '@ngrx/store';
import {
  Account,
  AccountDetails,
  AccountNotes,
  AccountSearch,
  AccountsState,
  AccountStudents,
  AccountSubscription,
  AccountTeachers,
  AccountUsage
} from './accounts.state';
import { LanguageType } from 'src/app/shared/objects/languages.object';
import { LicenceSummary } from '../services/models/subscriptions.model';

const selector = <T>(mapping: (state: AccountsState) => T) => createSelector(selectAccountsState, mapping);

export const selectAccountsState = createFeatureSelector<AccountsState>('accounts');

export const getAccountsState = selector((state: AccountsState): AccountsState => state);

export const getSearchResponse = createSelector(
  selectAccountsState,
  (state: AccountsState): AccountSearch => state.accountLookup
);

export const getSelectedAccount = createSelector(
  selectAccountsState,
  (state: AccountsState): Account => state.selectedAccount
);

export const getSelectedAccountDetails = createSelector(
  selectAccountsState,
  (state: AccountsState): AccountDetails => state.selectedAccount.accountDetails
);

export const getSelectedAccountID = createSelector(
  selectAccountsState,
  (state: AccountsState): number => state.selectedAccount.accountId
);

export const getSelectedAccountSubscriptions = createSelector(
  selectAccountsState,
  (state: AccountsState): AccountSubscription => state.selectedAccount.accountSubscriptions
);

export const getSelectedAccountLicencesRemaining = createSelector(
  selectAccountsState,
  (state: AccountsState): LicenceSummary[] => state.selectedAccount.accountSubscriptions.licenceSummaries
);

export const getSelectedAccountNotes = createSelector(
  selectAccountsState,
  (state: AccountsState): AccountNotes => state.selectedAccount.accountNotes
);

export const getSelectedAccountTeachers = createSelector(
  selectAccountsState,
  (state: AccountsState): AccountTeachers => state.selectedAccount.teachers
);

export const getSelectedTeacherFilter = createSelector(
  selectAccountsState,
  (state: AccountsState): string[] => state.selectedAccount.teachers.filter
);

export const getSelectedAccountLanguage = createSelector(
  selectAccountsState,
  (state: AccountsState): LanguageType => state.selectedAccount.accountDetails.language
);

export const getIsAccountAnonymised = createSelector(
  selectAccountsState,
  (state: AccountsState): boolean => state.selectedAccount.accountDetails.isAnonymised
);

export const getIsAccountDeleted = createSelector(
  selectAccountsState,
  (state: AccountsState): boolean => state.selectedAccount.accountDetails.isDeleted
);

export const getSelectedAccountStudents = createSelector(
  selectAccountsState,
  (state: AccountsState): AccountStudents => state.selectedAccount.students
);

export const getAccountSearchForm = createSelector(
  selectAccountsState,
  (
    state: AccountsState
  ): { pageSize: number; searchTerm: string; selectedProperty: string; showAnonymizedAccounts: boolean } =>
    state.accountLookup.accountSearchForm
);

export const getAccountUsage = createSelector(
  selectAccountsState,
  (state: AccountsState): AccountUsage => state.selectedAccount.usage
);
